import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useCart } from "../hooks/useCart";
import {
  updateQty,
  removeItem,
  buildCartMessage,
  waLink,
  clearCart,
} from "../lib/cart";
import { formatIDR } from "../data/products";
import { Trash2, MessageCircle, ShoppingBag } from "lucide-react";

const CartDrawer = ({ open, onOpenChange }) => {
  const { items, total, count } = useCart();
  const message = buildCartMessage(items);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        className="bg-[hsl(var(--background))] border-l border-[hsl(var(--border))] w-full sm:w-[460px] p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[hsl(var(--border))]">
          <SheetTitle className="font-display text-2xl flex items-center gap-3">
            Your Bag
            <span className="text-sm text-[hsl(var(--muted-foreground))] font-sans font-normal">
              ({count})
            </span>
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingBag className="w-10 h-10 text-[hsl(var(--ink))]/40 mb-4" />
            <p className="font-display text-xl">Your bag is empty</p>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xs">
              Add favorites from the collection to start your order via
              WhatsApp.
            </p>
            <button
              onClick={() => onOpenChange(false)}
              data-testid="cart-start-shopping"
              className="btn-primary mt-7"
            >
              Browse Collection
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5" data-testid="cart-items">
              {items.map((it) => (
                <div
                  key={it.key}
                  data-testid={`cart-line-${it.productId}`}
                  className="flex gap-4"
                >
                  <div className="w-20 h-24 bg-[hsl(var(--sand))] shrink-0 overflow-hidden">
                    <img
                      src={it.image}
                      alt={it.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base truncate">{it.name}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                      {[it.size, it.color].filter(Boolean).join(" · ")}
                    </p>
                    <p className="text-sm font-medium mt-1">
                      {formatIDR(it.price)}
                    </p>
                    <div className="flex items-center justify-between mt-3">
                      <div className="inline-flex items-center border border-[hsl(var(--border))] rounded-full text-sm">
                        <button
                          onClick={() => updateQty(it.key, it.qty - 1)}
                          data-testid={`cart-qty-minus-${it.productId}`}
                          className="w-7 h-7"
                        >
                          −
                        </button>
                        <span className="w-7 text-center" data-testid={`cart-qty-${it.productId}`}>
                          {it.qty}
                        </span>
                        <button
                          onClick={() => updateQty(it.key, it.qty + 1)}
                          data-testid={`cart-qty-plus-${it.productId}`}
                          className="w-7 h-7"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.key)}
                        data-testid={`cart-remove-${it.productId}`}
                        className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--ink))]"
                        aria-label="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[hsl(var(--border))] px-6 py-5 bg-[hsl(var(--cream))]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[hsl(var(--muted-foreground))]">
                  Estimated total
                </span>
                <span
                  className="font-display text-xl"
                  data-testid="cart-total"
                >
                  {formatIDR(total)}
                </span>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed mb-4">
                Availability, size, color, and final total will be confirmed by
                our team via WhatsApp.
              </p>
              <a
                href={waLink(message)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cart-checkout-whatsapp"
                className="btn-primary w-full justify-center"
              >
                <MessageCircle className="w-4 h-4" />
                Complete Order via WhatsApp
              </a>
              <button
                onClick={clearCart}
                data-testid="cart-clear"
                className="mt-3 w-full text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--ink))] underline underline-offset-4"
              >
                Clear bag
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
